import React, { useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Circle, Marker, Tooltip, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { statusColor } from "../lib/status";
import { getRoute, placeAutocomplete } from "../lib/api";

const CENTER = [13.16, -59.56];

const SERVICE = {
  Police: { color: "#2a7fd6", ic: "fa-shield-halved" },
  Traffic: { color: "#e0a300", ic: "fa-traffic-light" },
  Fire: { color: "#e0491c", ic: "fa-truck-fast" },
  Ambulance: { color: "#0f9d58", ic: "fa-truck-medical" },
  "Coast Guard": { color: "#17a2b8", ic: "fa-anchor" },
};
const STATUS_DOT = { available: "#12b34a", enroute: "#e0a300", deployed: "#da291c", out_of_service: "#5b7186" };
const SEV_COLOR = { high: "#da291c", medium: "#e0491c", low: "#e0a300" };

function PlaceField({ value, onChange, placeholder, icon, color, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const timer = React.useRef(null);
  const skip = React.useRef(false);

  const handleChange = (v) => {
    onChange(v);
    if (skip.current) { skip.current = false; return; }
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await placeAutocomplete(v);
        setSuggestions(r.suggestions || []);
        setOpen((r.suggestions || []).length > 0);
        setActive(-1);
      } catch (_) { setSuggestions([]); setOpen(false); }
    }, 250);
  };
  const pick = (desc) => { skip.current = true; onChange(desc); setOpen(false); setSuggestions([]); };

  return (
    <div className="rp-field rp-autocomplete">
      <i className={`fa-solid ${icon}`} style={{ color }} />
      <input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(suggestions[active].description); }
          else if (e.key === "Escape") setOpen(false);
        }}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
      />
      {open && (
        <ul className="rp-suggestions" data-testid={`${testid}-suggestions`}>
          {suggestions.map((s, i) => (
            <li key={s.place_id || i} className={i === active ? "on" : ""}
              onMouseDown={(e) => { e.preventDefault(); pick(s.description); }}
              onMouseEnter={() => setActive(i)} data-testid={`${testid}-suggestion-${i}`}>
              <i className="fa-solid fa-location-dot" /> {s.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function planeIcon(track) {
  const rot = (typeof track === "number" ? track : 0) - 45;
  return L.divIcon({ className: "plane-div-icon", html: `<i class="fa-solid fa-plane" style="transform:rotate(${rot}deg)"></i>`, iconSize: [22, 22], iconAnchor: [11, 11] });
}
function unitIcon(service, status) {
  const m = SERVICE[service] || { color: "#5b7186", ic: "fa-location-dot" };
  const dot = STATUS_DOT[status] || "#5b7186";
  return L.divIcon({
    className: "unit-div-icon",
    html: `<span class="unit-pin" style="--uc:${m.color}"><i class="fa-solid ${m.ic}"></i><b class="unit-dot" style="background:${dot}"></b></span>`,
    iconSize: [30, 30], iconAnchor: [15, 27],
  });
}
function incidentIcon(sev) {
  const c = SEV_COLOR[sev] || "#e0a300";
  return L.divIcon({ className: "inc-div-icon", html: `<span class="inc-pulse" style="--ic:${c}"><i class="fa-solid fa-triangle-exclamation"></i></span>`, iconSize: [28, 28], iconAnchor: [14, 14] });
}
function camIcon() {
  return L.divIcon({ className: "cam-div-icon", html: `<span class="cam-pin"><i class="fa-solid fa-video"></i></span>`, iconSize: [20, 20], iconAnchor: [10, 10] });
}
function closureIcon() {
  return L.divIcon({ className: "clo-div-icon", html: `<span class="clo-pin"><i class="fa-solid fa-road-circle-xmark"></i></span>`, iconSize: [26, 26], iconAnchor: [13, 13] });
}
function stormIcon() {
  return L.divIcon({ className: "map-ov-icon", html: `<span class="ov-storm"><i class="fa-solid fa-hurricane"></i></span>`, iconSize: [28, 28], iconAnchor: [14, 14] });
}
function gatheringIcon(variant) {
  return L.divIcon({ className: "map-ov-icon", html: `<span class="ov-gathering like-${variant || "low"}"><i class="fa-solid fa-people-group"></i></span>`, iconSize: [26, 26], iconAnchor: [13, 13] });
}

function decodePolyline(str) {
  let index = 0, lat = 0, lng = 0, coords = [];
  while (index < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    coords.push([lat / 1e5, lng / 1e5]);
  }
  return coords;
}

function FitRoute({ coords }) {
  const map = useMap();
  React.useEffect(() => { if (coords && coords.length) map.fitBounds(coords, { padding: [40, 40] }); }, [coords, map]);
  return null;
}
function ViewController({ showPlanes, aircraft, hasRoute, fitPts, focusCoord }) {
  const map = useMap();
  React.useEffect(() => {
    if (focusCoord) { map.setView(focusCoord, 15, { animate: true }); return; }
    if (hasRoute) return;
    if (showPlanes && aircraft.length) {
      const pts = [CENTER, ...aircraft.filter((a) => a.lat != null && a.lon != null).map((a) => [a.lat, a.lon])];
      map.fitBounds(pts, { padding: [60, 60] });
    } else if (fitPts && fitPts.length) {
      map.fitBounds(fitPts, { padding: [70, 70], maxZoom: 13.5 });
    } else {
      map.setView(CENTER, 12);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPlanes, hasRoute, focusCoord && focusCoord.join(",")]);
  return null;
}

const LayerToggle = ({ on, onClick, icon, label, testid }) => (
  <button className={`layer-toggle ${on ? "on" : ""}`} onClick={onClick} data-testid={testid} title={label}>
    <i className={`fa-solid ${icon}`} /> {label}
  </button>
);

const LiveMap = ({ routes = [], aircraft = [], overlays = {}, fullscreen = false, focusCoord = null, focusLabel = "", onBack }) => {
  const [origin, setOrigin] = useState("Warrens, Barbados");
  const [dest, setDest] = useState("Bridgetown, Barbados");
  const [coords, setCoords] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(null);

  const [showPlanes, setShowPlanes] = useState(false);
  const [layers, setLayers] = useState({
    traffic: true, incidents: true, police: true, ambulance: true, fire: true, field: true,
    citizen: true, closures: true, cameras: fullscreen, weather: true, flood: true, crowd: true,
  });
  const toggle = (k) => setLayers((s) => ({ ...s, [k]: !s[k] }));
  const svc = (u) => (u.service || "").toLowerCase();

  const corridors = overlays.corridors || [];
  const incidents = overlays.incidents || [];
  const units = overlays.units || [];
  const monitoring = overlays.monitoring || [];
  const closures = overlays.closures || [];
  const gatherings = overlays.gatherings || [];
  const storms = overlays.storms || [];
  const marineRing = overlays.marine_ring;

  const findRoute = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await getRoute(origin, dest);
      setCoords(decodePolyline(r.polyline));
      setInfo({ duration: r.duration, distance: r.distance, summary: r.summary });
    } catch (e) {
      setErr("Could not find a route for those locations.");
      setCoords(null); setInfo(null);
    } finally { setBusy(false); }
  };

  const corridorPath = (c) => (c.polyline ? decodePolyline(c.polyline) : c.path);

  const fitPts = [];
  corridors.forEach((c) => { const p = corridorPath(c); if (p) p.forEach((pt) => fitPts.push(pt)); });
  incidents.forEach((o) => fitPts.push([o.lat, o.lng]));
  units.forEach((o) => fitPts.push([o.lat, o.lng]));

  return (
    <div className={`panel navy entrance map-panel ${fullscreen ? "map-fullscreen" : ""}`} data-testid="live-map-panel">
      <div className="panel-head">
        <div className="panel-title">
          {fullscreen && onBack && (
            <button className="btn btn-back-cc" onClick={onBack} data-testid="back-to-command-centre">
              <i className="fa-solid fa-arrow-left" /> Back to Command Centre
            </button>
          )}
          <div className="p-ic"><i className="fa-solid fa-map-location-dot" /></div>
          {fullscreen ? "Operations Map" : "Live Operations Map"}
          {focusLabel && <span className="map-focus-label"><i className="fa-solid fa-crosshairs" /> {focusLabel}</span>}
        </div>
        <span className="map-tag-inline">
          <span className="live-dot" /> Live
          <button className={`plane-toggle ${showPlanes ? "on" : ""}`} onClick={() => setShowPlanes((v) => !v)} data-testid="toggle-planes" title="Toggle live aircraft">
            <i className="fa-solid fa-plane" /> {aircraft.length}
          </button>
        </span>
      </div>

      <div className="map-layerbar" data-testid="map-layers">
        <LayerToggle on={layers.traffic} onClick={() => toggle("traffic")} icon="fa-road" label="Traffic" testid="layer-traffic" />
        <LayerToggle on={layers.incidents} onClick={() => toggle("incidents")} icon="fa-triangle-exclamation" label="Incidents" testid="layer-incidents" />
        <LayerToggle on={layers.police} onClick={() => toggle("police")} icon="fa-shield-halved" label="Police" testid="layer-police" />
        <LayerToggle on={layers.ambulance} onClick={() => toggle("ambulance")} icon="fa-truck-medical" label="Ambulance" testid="layer-ambulance" />
        <LayerToggle on={layers.fire} onClick={() => toggle("fire")} icon="fa-truck-fast" label="Fire" testid="layer-fire" />
        <LayerToggle on={layers.field} onClick={() => toggle("field")} icon="fa-user-shield" label="Field Officers" testid="layer-field" />
        <LayerToggle on={layers.citizen} onClick={() => toggle("citizen")} icon="fa-user" label="Citizen Reports" testid="layer-citizen" />
        <LayerToggle on={layers.closures} onClick={() => toggle("closures")} icon="fa-road-circle-xmark" label="Road Closures" testid="layer-closures" />
        <LayerToggle on={layers.cameras} onClick={() => toggle("cameras")} icon="fa-video" label="Cameras" testid="layer-cameras" />
        <LayerToggle on={layers.weather} onClick={() => toggle("weather")} icon="fa-hurricane" label="Weather" testid="layer-weather" />
        <LayerToggle on={layers.flood} onClick={() => toggle("flood")} icon="fa-water" label="Flood Risk" testid="layer-flood" />
        <LayerToggle on={layers.crowd} onClick={() => toggle("crowd")} icon="fa-people-group" label="Crowd / Event" testid="layer-crowd" />
      </div>

      <div className="route-planner" data-testid="route-planner">
        <PlaceField value={origin} onChange={setOrigin} placeholder="From" icon="fa-circle-dot" color="#12b34a" testid="route-origin" />
        <PlaceField value={dest} onChange={setDest} placeholder="To" icon="fa-location-dot" color="#ff5a4d" testid="route-dest" />
        <button className="btn btn-primary rp-btn" onClick={findRoute} disabled={busy} data-testid="find-route-btn">
          <i className={`fa-solid ${busy ? "fa-spinner fa-spin" : "fa-route"}`} /> Fastest route
        </button>
        {info && <div className="rp-result" data-testid="route-result"><b>{info.duration}</b> · {info.distance} {info.summary && <span>via {info.summary}</span>}</div>}
        {err && <div className="rp-result err" data-testid="route-error">{err}</div>}
      </div>

      <div className="map-wrap" data-testid="live-map">
        <MapContainer center={CENTER} zoom={12} scrollWheelZoom style={{ width: "100%", height: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />

          {/* Coloured corridors following real road geometry */}
          {layers.traffic && corridors.map((c) => {
            const path = corridorPath(c);
            if (!path || !path.length) return null;
            const col = statusColor(c.status);
            return (
              <React.Fragment key={`cor-${c.id}`}>
                <Polyline positions={path} pathOptions={{ color: col, weight: 12, opacity: 0.16, lineCap: "round" }} />
                <Polyline positions={path} pathOptions={{ color: col, weight: 5, opacity: 0.95, lineCap: "round" }} data-testid={`corridor-${c.id}`}>
                  <Tooltip sticky>{c.name} · {c.status} · +{c.delay_minutes} min</Tooltip>
                </Polyline>
              </React.Fragment>
            );
          })}

          {/* Route planner result */}
          {coords && <Polyline positions={coords} pathOptions={{ color: "#0b57d0", weight: 6, opacity: 0.9 }} />}
          {coords && <FitRoute coords={coords} />}
          <ViewController showPlanes={showPlanes} aircraft={aircraft} hasRoute={!!coords} fitPts={fitPts} focusCoord={focusCoord} />

          {/* Marine / flood-risk coastal warning ring */}
          {layers.flood && marineRing && (
            <Circle center={[marineRing.lat, marineRing.lng]} radius={marineRing.radius_m}
              pathOptions={{ color: "#17a2b8", weight: 1.5, dashArray: "6 8", fillColor: "#17a2b8", fillOpacity: 0.05 }}>
              <Tooltip>{marineRing.label}</Tooltip>
            </Circle>
          )}

          {/* Incident radius + blinking marker (official = Incidents layer, public = Citizen Reports layer) */}
          {incidents.filter((o) => (o.source === "public" ? layers.citizen : layers.incidents)).map((o, i) => (
            <React.Fragment key={`inc-${o.id || i}`}>
              <Circle center={[o.lat, o.lng]} radius={o.radius_m || 500}
                pathOptions={{ color: SEV_COLOR[o.severity] || "#e0a300", weight: 1, fillColor: SEV_COLOR[o.severity] || "#e0a300", fillOpacity: 0.12 }} />
              <Marker position={[o.lat, o.lng]} icon={incidentIcon(o.severity)} data-testid={`ov-incident-${i}`}
                eventHandlers={{ click: () => setSel(o) }}>
                <Tooltip direction="top" offset={[0, -12]}>
                  <b>{o.type}</b> · {o.severity}{o.verified ? " · verified" : " · unverified"}<br />{o.location}<br /><i>Click for details</i>
                </Tooltip>
              </Marker>
            </React.Fragment>
          ))}

          {/* Road closures */}
          {layers.closures && closures.map((o, i) => (
            <Marker key={`clo-${i}`} position={[o.lat, o.lng]} icon={closureIcon()} data-testid={`ov-closure-${i}`}>
              <Tooltip direction="top" offset={[0, -10]}><b>Road closed</b><br />{o.label}</Tooltip>
            </Marker>
          ))}

          {/* Storm / weather marker */}
          {layers.weather && storms.map((o, i) => (
            <Marker key={`storm-${i}`} position={[o.lat, o.lng]} icon={stormIcon()} data-testid={`ov-storm-${i}`}>
              <Tooltip direction="top" offset={[0, -10]}><b>Tropical system · {o.status}</b><br />{o.label}{o.approx ? <><br /><i>Position approximate</i></> : null}</Tooltip>
            </Marker>
          ))}

          {/* Crowd / event activity */}
          {layers.crowd && gatherings.map((o, i) => (
            <Marker key={`gath-${i}`} position={[o.lat, o.lng]} icon={gatheringIcon(o.likelihood)} data-testid={`ov-gathering-${i}`}>
              <Tooltip direction="top" offset={[0, -10]}><b>{o.type}</b> · {o.likelihood} likelihood<br />{o.title}<br /><i>{o.location}</i></Tooltip>
            </Marker>
          ))}

          {/* Traffic cameras (real monitoring nodes) */}
          {layers.cameras && monitoring.map((o, i) => (
            <Marker key={`cam-${i}`} position={[o.lat, o.lng]} icon={camIcon()} data-testid={`ov-camera-${i}`}>
              <Tooltip direction="top" offset={[0, -8]}>Traffic monitoring point<br />{o.name}</Tooltip>
            </Marker>
          ))}

          {/* Emergency units by service (Police / Ambulance / Fire / Field Officers) */}
          {units.filter((u) => {
            const s = svc(u);
            if (s === "police") return layers.police;
            if (s === "ambulance") return layers.ambulance;
            if (s === "fire") return layers.fire;
            return layers.field; // traffic / coast guard = field officers
          }).map((u, i) => (
            <Marker key={`unit-${u.id || i}`} position={[u.lat, u.lng]} icon={unitIcon(u.service, u.status)} data-testid={`ov-unit-${i}`}>
              <Tooltip direction="top" offset={[0, -22]}>
                <b>{u.callsign}</b> · {u.service}<br />Status: {u.status}{u.assignment ? <><br />{u.assignment}</> : null}
              </Tooltip>
            </Marker>
          ))}

          {/* Live aircraft */}
          {showPlanes && aircraft.map((a, i) => (
            (a.lat != null && a.lon != null) ? (
              <Marker key={a.reg || i} position={[a.lat, a.lon]} icon={planeIcon(a.track)} data-testid={`plane-${i}`}>
                <Tooltip direction="top" offset={[0, -8]}>
                  <b>{a.flight}</b> · {a.type}<br />
                  {a.alt_ft != null ? `${a.alt_ft.toLocaleString()} ft` : "—"} · {a.speed_kts != null ? `${a.speed_kts} kts` : "—"}<br />
                  {a.dist_km} km from BGI
                </Tooltip>
              </Marker>
            ) : null
          ))}
        </MapContainer>

        <div className="map-legend map-legend-rich">
          <div className="legend-row">
            <div className="legend-item"><span className="legend-line" style={{ background: "#009639" }} /> Light</div>
            <div className="legend-item"><span className="legend-line" style={{ background: "#e0a300" }} /> Moderate</div>
            <div className="legend-item"><span className="legend-line" style={{ background: "#da291c" }} /> Heavy</div>
          </div>
          <div className="legend-row">
            <div className="legend-item"><i className="fa-solid fa-triangle-exclamation" style={{ color: "#da291c" }} /> Incident</div>
            <div className="legend-item"><i className="fa-solid fa-shield-halved" style={{ color: "#2a7fd6" }} /> Unit</div>
            <div className="legend-item"><i className="fa-solid fa-road-circle-xmark" style={{ color: "#ff5a4d" }} /> Closure</div>
            <div className="legend-item"><i className="fa-solid fa-video" style={{ color: "#8ec8f2" }} /> Camera</div>
          </div>
        </div>

        {sel && (
          <div className="inc-context" data-testid="incident-context-panel">
            <div className="inc-ctx-head">
              <span><i className="fa-solid fa-triangle-exclamation" /> Incident</span>
              <button className="modal-close" onClick={() => setSel(null)} data-testid="incident-context-close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="inc-ctx-body">
              <div className="ic-row"><span>Incident ID</span><b>{sel.id ? String(sel.id).slice(0, 10) : "—"}</b></div>
              <div className="ic-row"><span>Type</span><b>{sel.type || "—"}</b></div>
              <div className="ic-row"><span>Location</span><b>{sel.location || "—"}</b></div>
              <div className="ic-row"><span>Priority</span><b className="cap">{sel.severity || "—"}</b></div>
              <div className="ic-row"><span>Reported</span><b>{sel.minutes_ago != null ? `${sel.minutes_ago} min ago` : "—"}</b></div>
              <div className="ic-row"><span>Verification</span><b>{sel.verified ? "Verified" : "Unverified"}</b></div>
              <div className="ic-row"><span>Source</span><b>{sel.source === "public" ? "Citizen report" : "Official"}</b></div>
              <div className="ic-row"><span>Nearby resources</span><b>{units.length} units on grid</b></div>
              <div className="ic-note"><i className="fa-solid fa-list-check" /> Recommended: {sel.severity === "high" ? "Dispatch nearest unit and issue a public advisory." : "Verify and monitor; dispatch if it escalates."}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveMap;
